import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { erpApi, type ErpPayout } from '../../api/erp';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import { formatMoney } from '../../utils/numbers';
import './erp.css';

function payoutStatusVariant(status: ErpPayout['status']) {
  if (status === 'paid') return 'success';
  if (status === 'cancelled') return 'danger';
  return 'warning';
}

function payoutStatusLabel(status: ErpPayout['status']) {
  switch (status) {
    case 'paid':
      return 'Pagado';
    case 'processing':
      return 'En proceso';
    case 'cancelled':
      return 'Cancelado';
    default:
      return 'Pendiente';
  }
}

export function ErpPayoutsPage() {
  const [payouts, setPayouts] = useState<ErpPayout[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payingId, setPayingId] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_reference: '',
    payment_method: 'transferencia',
    admin_notes: '',
  });

  async function loadPayouts(filter = statusFilter) {
    setLoading(true);
    setError('');
    try {
      const res = await erpApi.listPayouts(filter || undefined);
      setPayouts(res.payouts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar liquidaciones');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPayouts();
  }, []);

  async function handlePay(event: FormEvent, payoutId: number) {
    event.preventDefault();
    setPayingId(payoutId);
    setError('');
    try {
      await erpApi.markPayoutPaid(payoutId, {
        payment_reference: paymentForm.payment_reference || undefined,
        payment_method: paymentForm.payment_method || undefined,
        admin_notes: paymentForm.admin_notes || undefined,
      });
      setPayingId(null);
      setPaymentForm({ payment_reference: '', payment_method: 'transferencia', admin_notes: '' });
      await loadPayouts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo registrar el pago');
      setPayingId(null);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ margin: 0 }}>Liquidaciones</h1>
        <Select
          label=""
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            void loadPayouts(e.target.value);
          }}
          options={[
            { value: '', label: 'Todos los estados' },
            { value: 'pending', label: 'Pendientes' },
            { value: 'paid', label: 'Pagadas' },
          ]}
        />
      </div>

      {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}
      {loading ? <p>Cargando liquidaciones…</p> : null}

      {!loading && payouts.length === 0 ? (
        <Card title="Sin liquidaciones">
          <p className="muted" style={{ margin: 0 }}>
            Crea liquidaciones desde el detalle de cada agente cuando haya ganancias pendientes.
          </p>
        </Card>
      ) : null}

      <div className="erp-payout-list">
        {payouts.map((payout) => (
          <Card key={payout.id} title={`Liquidación #${payout.id}`}>
            <div className="erp-payout-header">
              <div>
                <p style={{ margin: 0 }}>
                  <Link to={`/erp/agentes/${payout.agent_id}`}>
                    {payout.agent_name} {payout.agent_surname}
                  </Link>
                </p>
                <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.85rem' }}>
                  {new Date(payout.period_start).toLocaleDateString('es-ES')} –{' '}
                  {new Date(payout.period_end).toLocaleDateString('es-ES')}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ fontSize: '1.2rem' }}>{formatMoney(payout.total_amount)}</strong>
                <div style={{ marginTop: '0.35rem' }}>
                  <Badge tone={payoutStatusVariant(payout.status)}>{payoutStatusLabel(payout.status)}</Badge>
                </div>
              </div>
            </div>

            <div className="erp-stats" style={{ marginTop: '1rem' }}>
              <div>
                <span className="muted">Soporte</span>
                <p style={{ margin: '0.2rem 0 0' }}>{formatMoney(payout.tickets_earnings)}</p>
              </div>
              <div>
                <span className="muted">Valoraciones</span>
                <p style={{ margin: '0.2rem 0 0' }}>{formatMoney(payout.rating_bonus)}</p>
              </div>
              <div>
                <span className="muted">Ventas</span>
                <p style={{ margin: '0.2rem 0 0' }}>{formatMoney(payout.sales_commission)}</p>
              </div>
              {(payout.referral_milestone_bonus ?? 0) > 0 ? (
                <div>
                  <span className="muted">Hitos referidos</span>
                  <p style={{ margin: '0.2rem 0 0' }}>{formatMoney(payout.referral_milestone_bonus)}</p>
                </div>
              ) : null}
            </div>

            {payout.status === 'pending' ? (
              payingId === payout.id ? (
                <form className="erp-form-grid" style={{ marginTop: '1rem' }} onSubmit={(e) => void handlePay(e, payout.id)}>
                  <Input
                    label="Referencia de pago"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, payment_reference: e.target.value }))}
                    placeholder="Ej. TRF-2026-001"
                  />
                  <Select
                    label="Método"
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, payment_method: e.target.value }))}
                    options={[
                      { value: 'transferencia', label: 'Transferencia bancaria' },
                      { value: 'bizum', label: 'Bizum' },
                      { value: 'otro', label: 'Otro' },
                    ]}
                  />
                  <label className="ui-field">
                    <span className="ui-field__label">Notas (opcional)</span>
                    <textarea
                      className="ui-textarea"
                      rows={2}
                      value={paymentForm.admin_notes}
                      onChange={(e) => setPaymentForm((p) => ({ ...p, admin_notes: e.target.value }))}
                    />
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button type="submit">Confirmar pago</Button>
                    <Button type="button" variant="ghost" onClick={() => setPayingId(null)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <div style={{ marginTop: '1rem' }}>
                  <Button size="sm" onClick={() => setPayingId(payout.id)}>
                    Registrar pago
                  </Button>
                </div>
              )
            ) : payout.paid_at ? (
              <p className="muted" style={{ margin: '1rem 0 0', fontSize: '0.85rem' }}>
                Pagado el {new Date(payout.paid_at).toLocaleDateString('es-ES')}
                {payout.payment_reference ? ` · Ref: ${payout.payment_reference}` : ''}
              </p>
            ) : null}
          </Card>
        ))}
      </div>
    </div>
  );
}
